import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

// Set default withCredentials globally:
axios.defaults.withCredentials = true;

const AuthContext = createContext({});

// Helper to normalize URLs so that default ports aren’t appended
const normalizeUrl = (url, port) => {
  // If port is 443 for HTTPS, or 80 for HTTP, don’t append it
  if ((url.startsWith('https://') && (port === '443' || port === 443)) ||
      (url.startsWith('http://') && (port === '80' || port === 80))) {
    return url;
  }
  return `${url}:${port}`;
};

export const AuthProvider = ({ children }) => {
  // Use environment variables injected by CRA (prefixed with REACT_APP_)
  const BASE_URL = process.env.REACT_APP_ENVIRONMENT === 'production' 
    ? process.env.REACT_APP_PRODUCTION_BASE_URL 
    : process.env.REACT_APP_DEVELOPMENT_BASE_URL;
  const PORT = process.env.REACT_APP_ENVIRONMENT === 'production' 
    ? process.env.REACT_APP_PRODUCTION_PORT 
    : process.env.REACT_APP_DEVELOPMENT_PORT;
  const API_BASE_URL = process.env.REACT_APP_ENVIRONMENT === 'production' 
    ? process.env.REACT_APP_PRODUCTION_API_BASE_URL 
    : process.env.REACT_APP_DEVELOPMENT_API_BASE_URL;

  const baseUrl = normalizeUrl(BASE_URL, PORT);
  const apiUrl = normalizeUrl(API_BASE_URL, PORT);

  const loggedOutOnly = ['/login'];
  const [user, setUser] = useState(null);
  const [userId, setUserId] = useState('');
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [userAvatar, setUserAvatar] = useState('');
  const [discordServers, setDiscordServers] = useState([]);
  const [error, setError] = useState(null);
  const [authInfo, setAuthInfo] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingInitial, setLoadingInitial] = useState(true);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (error) setError(null);
  }, [location.pathname]);

  // Fetch current user info using the JWT from cookies
  useEffect(() => {
    axios.get(`${apiUrl}/api/current-user`)
      .then(({ data: currentUser }) => {
        setUser(currentUser);
        setUserId(currentUser.id);
        setUsername(currentUser.username);
        setDisplayName(currentUser.display_name);
        setUserAvatar(currentUser.avatar);
        setDiscordServers(currentUser.discord_servers);
      })
      .catch((err) => {
        console.error('Error fetching current user:', err);
        setUser(null);
      })
      .finally(() => setLoadingInitial(false));
  }, [apiUrl]);

  // Login function (if needed; for Discord OAuth you may just redirect)
  const login = async (loginData) => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${apiUrl}/api/login`, loginData);
      if (data.success) {
        setUser(data.user);
        setAuthInfo({});
        const redirectPath = (loginData.redirect && !loggedOutOnly.includes(loginData.redirect))
          ? loginData.redirect
          : '/';
        navigate(redirectPath);
      } else {
        setAuthInfo(data);
      }
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = async () => {
    try {
      await axios.post(`${apiUrl}/api/logout`);
      setUser(undefined);
      navigate('/login');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const memoedValue = useMemo(() => ({
    user,
    userId,
    setUserId,
    username,
    setUsername,
    displayName,
    setDisplayName,
    userAvatar,
    discordServers,
    setDiscordServers,
    loading,
    error,
    authInfo,
    login,
    logout,
  }), [user, userId, username, displayName, userAvatar, discordServers, loading, error, authInfo]);

  return (
    <AuthContext.Provider value={memoedValue}>
      {!loadingInitial && children}
    </AuthContext.Provider>
  );
};

export default function useAuth() {
  return useContext(AuthContext);
}
